import { authenticated } from './WebRequest';
import { omitNil } from '../mylib/Utils';
import JSONRPC from './JSONRPC';
import type { JsonRpcTransport } from './types/JSONRPCAPI';
import type {
  ApiThunk,
  MaintenanceCaseParameters,
  MaintenanceId,
  MaintenanceListResponse,
  MaintenanceMachineParameters,
  MaintenanceQtCommandResponse,
  MaintenanceQtObject,
  MaintenanceServiceDataResponse,
  MaintenanceSiteParameters,
  SaveMaintenanceParameters,
  SetServiceDataParameters,
  UnseenMaintenanceResponse,
} from './types/Maintenance';

export type {
  ApiThunk,
  MaintenanceCase,
  MaintenanceCaseParameters,
  MaintenanceId,
  MaintenanceListResponse,
  MaintenanceMachineParameters,
  MaintenanceNoteStatus,
  MaintenanceQtCommandResponse,
  MaintenanceQtObject,
  MaintenanceServiceDataResponse,
  MaintenanceSiteParameters,
  SaveMaintenanceParameters,
  SetServiceDataParameters,
  UnseenMaintenanceCase,
  UnseenMaintenanceResponse,
} from './types/Maintenance';

function createNullJsonRpc(): JsonRpcTransport {
  return {
    request: <T = unknown>() => Promise.resolve(null as T),
    handleResponse: () => undefined,
  };
}

export class MaintenanceAPI {
  private jsonrpc: JsonRpcTransport;

  // dummy dispatcher that resolves all calls to null
  constructor() {
    // dummy dispatcher that resolves all calls to null
    this.jsonrpc = createNullJsonRpc();
  }

  connect(qtobject: MaintenanceQtObject | null): void {
    // connecting all maintenance actions here
    try {
      if (qtobject) {
        this.jsonrpc = new JSONRPC((message) =>
          qtobject.dispatch(JSON.stringify(message))
        );
        qtobject.response.connect((str: string) =>
          this.jsonrpc.handleResponse(JSON.parse(str))
        );
      }
    } catch (e) {
      console.log(e);
      this.jsonrpc = createNullJsonRpc();
    }
  }

  browseLogs(
    maintenanceid: MaintenanceId
  ): Promise<MaintenanceQtCommandResponse> {
    return this.jsonrpc.request<MaintenanceQtCommandResponse>('browse_logs', [
      maintenanceid,
    ]);
  }

  sendLogs(
    siteid: number,
    maintenanceid: MaintenanceId
  ): Promise<MaintenanceQtCommandResponse> {
    return this.jsonrpc.request<MaintenanceQtCommandResponse>('send_logs', [
      siteid,
      maintenanceid,
    ]);
  }

  get_site_maintenance(
    parameters: MaintenanceMachineParameters
  ): ApiThunk<MaintenanceListResponse> {
    const { siteid, machinedbid } = omitNil(parameters);
    return authenticated.get(
      `/maintenance/site/${siteid}/machine/${machinedbid}`
    );
  }

  save_intervention(
    parameters: SaveMaintenanceParameters
  ): ApiThunk<MaintenanceListResponse[number]> {
    const { siteid, machinedbid, ...params } = omitNil(parameters);
    return authenticated.post(
      `/maintenance/site/${siteid}/machine/${machinedbid}`,
      { params }
    );
  }

  set_service_data(
    parameters: SetServiceDataParameters
  ): ApiThunk<MaintenanceServiceDataResponse> {
    const { siteid, machinedbid, ...params } = omitNil(parameters);
    return authenticated.post(
      `/maintenance/site/${siteid}/machine/${machinedbid}/set_service_data`,
      { params }
    );
  }

  delete_maintenance_case(
    parameters: MaintenanceCaseParameters
  ): ApiThunk<MaintenanceListResponse[number]> {
    const { siteid, maintenanceid } = omitNil(parameters);
    return authenticated.delete(
      `/maintenance/site/${siteid}/maintenance/${maintenanceid}`
    );
  }

  get_unseen(
    parameters: MaintenanceSiteParameters
  ): ApiThunk<UnseenMaintenanceResponse> {
    const { siteid } = omitNil(parameters);

    return authenticated.get(`/maintenance/site/${siteid}/unseen`);
  }
}
const api = new MaintenanceAPI();

export { api as default };
